/**
 * AddressRow — one entry in the residential-address list. Switches
 * between read view and inline edit form via the row's `isEdit` flag.
 *
 * Flutter parity:
 *   - `residential_address_item_widget.dart` (read view)
 *   - `edit_residential_address_widget.dart` (edit form)
 *
 * Read view shows: location name, full address line, isDefault badge,
 * Edit + Remove buttons.
 *
 * Edit form composes the shared `AddressEditFields` component (location
 * name, autocomplete-or-manual address, apartment, notes, isDefault
 * toggle). The card chrome (blue border, X cancel, Remove/Done action
 * row) stays here — the appointment-context modal sub-view uses a
 * different chrome but the same `AddressEditFields` body.
 *
 * Save gate matches Flutter `create_edit_patient_bloc.dart:676-681`:
 * `locationName && address` both non-empty.
 *
 * Removing an address sets `isDeleted=true` rather than splicing the
 * array — the backend tombstones on next save. Empty (never-saved) rows
 * are spliced by the parent list when cancelled.
 */

import { Pencil, Trash2, X } from 'lucide-react';
import { Button, StatusChip } from '@scripsteam/scrips-signal-ds';
import { RecordRowCard, RecordRowLine } from '@/shared/ui/components/record-row-card';
import { cn } from '@/lib/utils';
import {
  AddressEditFields,
  isAddressSaveEnabled,
} from './address-edit-fields';
import type { AddressFormValues } from './contact-details-schema';

export interface AddressRowProps {
  row: AddressFormValues;
  index: number;
  /** True when this is the only non-deleted row (controls Remove disable). */
  isOnly: boolean;
  readOnly?: boolean;
  /** Toggle the row's `isEdit` flag (parent owns the patch). */
  onChange: (next: AddressFormValues) => void;
  /** Cancel pending edits on a row that's still being filled in. The
   *  parent decides whether to splice it (new row) or restore the
   *  pre-edit snapshot (existing row with a contactId). */
  onCancel: () => void;
  /** Soft-delete this row (sets isDeleted=true). */
  onRemove: () => void;
}

export function AddressRow({
  row,
  index,
  isOnly,
  readOnly,
  onChange,
  onCancel,
  onRemove,
}: AddressRowProps) {
  if (row.isDeleted) return null;
  if (row.isEdit) {
    return (
      <AddressEditCard
        row={row}
        index={index}
        readOnly={readOnly}
        onChange={onChange}
        onCancel={onCancel}
        onRemove={onRemove}
        isOnly={isOnly}
      />
    );
  }
  return (
    <AddressReadCard
      row={row}
      readOnly={readOnly}
      onEdit={() => onChange({ ...row, isEdit: true })}
      onRemove={onRemove}
      isOnly={isOnly}
    />
  );
}

// ── Read view ─────────────────────────────────────────────────────────────

// The approved design's PaRecordRowCard: the location name with its chip, the address and the notes as quiet lines,
// edit and remove on the right. Same buttons, handlers and disabled rule as before.
function AddressReadCard({
  row,
  readOnly,
  onEdit,
  onRemove,
  isOnly,
}: {
  row: AddressFormValues;
  readOnly?: boolean;
  onEdit: () => void;
  onRemove: () => void;
  isOnly: boolean;
}) {
  const lines = [row.apartment, row.address, row.city, row.country]
    .map((p) => p?.trim())
    .filter((p) => p && p.length > 0)
    .join(', ');

  return (
    <RecordRowCard
      title={row.locationName || 'Untitled address'}
      chips={
        row.isDefault ? (
          <StatusChip semantic="info" tone="soft">
            Default for home visits
          </StatusChip>
        ) : null
      }
      lines={
        <>
          <RecordRowLine>{lines || 'No address entered yet'}</RecordRowLine>
          {row.notes ? <RecordRowLine>{row.notes}</RecordRowLine> : null}
        </>
      }
      actions={
        !readOnly ? (
          <>
            <button type="button" onClick={onEdit} className="icon-action" aria-label="Edit address">
              <Pencil className="size-4" />
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={isOnly}
              className={cn('icon-action', isOnly && 'opacity-40 cursor-not-allowed hover:bg-transparent')}
              aria-label="Remove address"
            >
              <Trash2 className="size-4" />
            </button>
          </>
        ) : null
      }
    />
  );
}

// ── Edit view ─────────────────────────────────────────────────────────────

// The row opened into its fields (design PaRecordRowCard `editing`): the brand edge, the title kept with the cancel
// button at the right, the fields, then Done (primary) and Remove (tertiary).
function AddressEditCard({
  row,
  index,
  readOnly,
  onChange,
  onCancel,
  onRemove,
  isOnly,
}: {
  row: AddressFormValues;
  index: number;
  readOnly?: boolean;
  onChange: (next: AddressFormValues) => void;
  onCancel: () => void;
  onRemove: () => void;
  isOnly: boolean;
}) {
  return (
    <RecordRowCard
      editing
      title={row.contactId ? `Edit address ${index + 1}` : `New address ${index + 1}`}
      actions={
        <button type="button" onClick={onCancel} className="icon-action" aria-label="Cancel edit">
          <X className="size-4" />
        </button>
      }
      editActions={
        <>
          <Button
            type="button"
            size="sm"
            onClick={() => onChange({ ...row, isEdit: false })}
            disabled={readOnly || !isAddressSaveEnabled(row)}
          >
            Done
          </Button>
          <Button type="button" variant="tertiary" size="sm" onClick={onRemove} disabled={isOnly || readOnly}>
            Remove
          </Button>
        </>
      }
    >
      <AddressEditFields row={row} readOnly={readOnly} onChange={onChange} />
    </RecordRowCard>
  );
}
